import { z } from 'zod';

/**
 * Modello dei dati su misura (ADR 0005): i tipi di cose che l'azienda gestisce, ricavati dal motore, con le loro
 * entità nell'archivio. Ogni tipo ha l'attributo `nome`, che distingue un'entità dalle altre.
 */
export interface DataAttributeDto {
  nome: string;
  etichetta: string;
  tipo: 'testo' | 'numero' | 'data';
  descrizione: string | null;
  /** Cosa dipende da questo attributo (un obbligo, un documento, una procedura). */
  perche: string | null;
  /**
   * Legame con un'altra cosa dell'azienda: chiave di un altro tipo del modello, o "persona" per le persone che ci
   * lavorano. Il valore è il nome dell'entità collegata (più nomi separati da "; " se `multiplo`).
   */
  riferisce?: string | null;
  multiplo?: boolean;
  /** Il valore è un indirizzo postale: si salva come lo completa Google (ADR 0074). */
  indirizzo?: boolean;
  /**
   * Come si scrive il valore (ADR 0192), da un elenco fisso del sistema: testo, numero, importo, data, iban,
   * codice_fiscale, partita_iva, provincia, cap, comune, telefono, email, web, indirizzo, catastale.
   */
  formato?: string;
  /** L'attributo identifica una voce: due voci con lo stesso valore sono la stessa cosa (ADR 0192). */
  identita?: boolean;
}

export interface DataEntityDto {
  /** Riferimento stabile dell'entità. */
  rif: string;
  nome: string;
  attributi: Record<string, string | number | null>;
  /** Da dove viene ogni attributo ("persona" se l'ha scritto la persona). */
  fonti: Record<string, string>;
  /** Attributi del tipo ancora senza valore. */
  mancanti: string[];
  /** Sedi e attività economiche con cui la cosa ha a che fare (ADR 0057). */
  legami: DataEntityLinkDto[];
  /** Nessuna sede, o una sede con l'attività ancora da attribuire. */
  da_attribuire: boolean;
  /** Altri nomi con cui è stata trovata (doppioni uniti, ADR 0058). */
  altri_nomi: string[];
}

/**
 * Legame di un'entità (ADR 0057): una sede (null = l'impresa come soggetto) e un'attività economica di quella sede
 * (null = da attribuire). `escluso` = la persona ha detto di no, nessuna lettura lo rimette.
 */
export interface DataEntityLinkDto {
  sede_id: string | null;
  sede: string | null;
  attivita_id: string | null;
  attivita: string | null;
  stato: 'attivo' | 'escluso';
  deciso_da: 'motore' | 'dedotto' | 'regola' | 'persona';
  fonte: string;
  motivo: string | null;
}

export interface DataTypeDto {
  chiave: string;
  etichetta: string;
  descrizione: string | null;
  perche: string;
  attributi: DataAttributeDto[];
  /** unito = entrato in un altro tipo (ADR 0168): non si ripropone. */
  stato: 'attivo' | 'non_serve' | 'unito';
  /** Per un tipo unito: il tipo in cui è entrato. */
  unito_in: string | null;
  /** Unione proposta dal controllo dei doppioni, da confermare (ADR 0168). */
  unione_proposta: { in: string; perche: string } | null;
  /** Il ruolo tra i documenti dei rapporti (ADR 0168, 0169), es. fattura_acquisto; null per gli altri tipi. */
  ruolo_documento: 'fattura_vendita' | 'fattura_acquisto' | 'contratto_vendita' | 'contratto_acquisto' | null;
  entita: DataEntityDto[];
  /** La persona ha confermato che l'elenco è completo (null finché non lo dice). */
  completo_il: string | null;
  /** Le sue entità sono prodotti con istruzioni del produttore (manuale, manutenzione) da cercare online. */
  info_produttore: boolean;
  /** Le sue entità sono soggetti esterni: hanno P.IVA e codice fiscale e si uniscono i doppioni (ADR 0058). */
  soggetto_esterno: boolean;
}

export interface DataModelState {
  data: DataTypeDto[];
  generating: boolean;
  generated_at: string | null;
  error: string | null;
  /** Serve sapere qualcosa dell'azienda (sezione Azienda) prima di ricavare il modello. */
  needs_profile: boolean;
  /** Tipi nati dopo documenti o posta già letti, da cercare in quelle letture (ADR 0149). */
  da_rileggere: Array<{ chiave: string; etichetta: string }>;
}

/** Un collegamento della scheda di un'entità con un'altra cosa (ADR 0059). */
export interface EntityRelationDto {
  /** uscente = un suo valore nomina l'altra; entrante = l'altra la nomina; stesso_soggetto = la stessa in un altro tipo. */
  verso: 'uscente' | 'entrante' | 'stesso_soggetto';
  /** L'attributo che fa da legame (etichetta); null per lo stesso soggetto. */
  attributo: string | null;
  /** Tipo dell'altra cosa ("persona" per le persone dell'azienda). */
  tipo: string;
  etichetta_tipo: string;
  /** Riferimento dell'altra entità (null se il nome non corrisponde a nessuna nota). */
  rif: string | null;
  nome: string;
}

/** Scheda di un'entità (ADR 0059): valori, relazioni, documenti e posta che la riguardano. */
export interface EntityCardDto {
  tipo: Pick<DataTypeDto, 'chiave' | 'etichetta' | 'descrizione' | 'attributi' | 'soggetto_esterno'>;
  entita: DataEntityDto;
  relazioni: EntityRelationDto[];
  /** Documenti, messaggi ed entità più vicini per significato (ADR 0073). */
  vicini: Array<{ fonte: 'documento' | 'entita'; id: string; titolo: string; tipo: string | null; etichetta: string | null; riferimento: string | null; somiglianza: number }>;
  /** Documenti del rapporto (ADR 0060), quelli da cui risultano i suoi valori (fonte) o legati a lei nell'archivio. */
  documenti: Array<{ id: string; titolo: string; tipo: string; data: string; perche: 'rapporto' | 'fonte' | 'collegato'; che_cosa: string | null; data_documento: string | null }>;
  /** Documenti che la nominano nel testo, con il passaggio. */
  citazioni: Array<{ id: string; titolo: string; tipo: string; data: string; passaggio: string | null }>;
  /** Messaggi di posta che la nominano; null per chi non vede la posta. */
  posta: Array<{
    rif: string;
    oggetto: string | null;
    da: string | null;
    data: string | null;
    casella: string | null;
    passaggio: string | null;
    /** Legato al rapporto (ADR 0060), con che cosa è; altrimenti lo nomina soltanto. */
    legato?: boolean;
    che_cosa?: string | null;
  }> | null;
  /** Con quali parole è stata cercata nei testi. */
  cercata_come: string[];
  /**
   * Righe dei programmi collegati che la nominano (ADR 0120), un elenco per tipo del programma: le colonne legate al
   * suo tipo hanno il suo nome, un altro nome o un suo codice. Le più recenti, con il totale.
   */
  programmi: Array<{
    tipo: string;
    etichetta: string;
    /** Il programma che ne è master. */
    programma: string;
    colonne: Array<{ nome: string; etichetta: string }>;
    /** Le colonne che la nominano. */
    legata_da: string[];
    righe: Array<{ rif: string; valori: Record<string, unknown> }>;
    totale: number;
  }>;
}

/** Recupero dei documenti già letti (ADR 0060): stima senza motore, poi collegamento su conferma. */
export interface DocumentLinksStateDto {
  fase: 'nessuna' | 'stima' | 'pronto' | 'collego' | 'finito' | 'errore';
  stima: {
    documenti: number;
    messaggi: number;
    /** Documenti e messaggi che nominano troppi soggetti (registri, elenchi): non si legano. */
    esclusi: number;
    token: number;
    costo_usd: number;
  } | null;
  /** Avanzamento del collegamento. */
  fatti: number;
  totale: number;
  legami: number;
  nuove_voci: number;
  errori: number;
  errore: string | null;
  aggiornato_il: string | null;
}

/** Rilettura dei documenti e della posta già letti per i tipi nuovi (ADR 0149): stima senza motore, poi rilettura. */
export interface TypeRereadStateDto {
  /** fermo = interrotta (es. tetto di spesa raggiunto): riparte da dove era con un nuovo avvio. */
  fase: 'nessuna' | 'stima' | 'pronto' | 'rileggo' | 'fermo' | 'finito' | 'errore';
  stima: {
    tipi: Array<{ chiave: string; etichetta: string; documenti: number; messaggi: number }>;
    documenti: number;
    messaggi: number;
    token: number;
    costo_usd: number;
  } | null;
  /** Avanzamento della rilettura. */
  fatti: number;
  totale: number;
  /** Voci riconosciute nelle fonti rilette (nuove o già note). */
  voci: number;
  errori: number;
  errore: string | null;
  aggiornato_il: string | null;
}

/** Completamento dei valori mancanti (ADR 0073): stima, poi lettura delle fonti su conferma. */
export interface CompletionStateDto {
  fase: 'nessuna' | 'pronto' | 'completo' | 'finito' | 'errore';
  stima: {
    /** Voci con valori mancanti. */
    voci: number;
    valori_mancanti: number;
    token: number;
    /** Costo massimo: le voci senza fonti non costano. */
    costo_usd: number;
  } | null;
  fatti: number;
  totale: number;
  /** Voci senza fonti da leggere. */
  senza_fonti: number;
  valori: number;
  /** Voci spostate nel rapporto che le loro fonti mostrano (ADR 0102). */
  spostate: number;
  errori: number;
  errore: string | null;
  aggiornato_il: string | null;
}

/** La persona unisce entità dello stesso tipo che sono lo stesso soggetto (ADR 0058). */
/** La persona sposta una voce in un altro tipo (ADR 0168): valori, altri nomi, legami e documenti vanno con lei. */
/** La persona sposta più voci insieme in un altro tipo (ADR 0168). */
export const DataEntitiesMoveSchema = z.object({
  rif: z.array(z.string().min(1).max(200)).min(1).max(500).describe('Riferimenti delle voci da spostare'),
  in: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/).describe('Chiave del tipo in cui vanno'),
});

export const DataEntityMoveSchema = z.object({ in: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/).describe('Chiave del tipo in cui va la voce') });

export const DataEntityMergeSchema = z.object({
  tieni: z.string().min(1).max(200).describe('Riferimento dell’entità da tenere'),
  unisci: z.array(z.string().min(1).max(200)).min(1).max(50).describe('Riferimenti delle entità da unire a quella tenuta'),
});

/** Doppioni possibili di un tipo, da far confermare alla persona. */
export interface DataDuplicatesDto {
  tipo: string;
  possibili: Array<{ entita: Array<{ rif: string; nome: string; partita_iva: string | null }>; perche: string }>;
}

export const DataTypeStatusSchema = z.object({ stato: z.enum(['attivo', 'non_serve']) });

/** La persona conferma un'unione proposta, o ne sceglie un'altra; null = la rifiuta (ADR 0168). */
export const DataTypeMergeSchema = z.object({ in: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/).nullable() });

const MappingPairSchema = z.object({ attributo: z.string().max(63), colonna: z.string().max(200) });

/** La persona scrive la mappatura di una lettura di un programma (ADR 0168). */
export const ProgramMappingSchema = z.object({
  riga: z.object({
    cosa: z.enum(['voce', 'senza_tipo', 'niente']),
    tipo: z.string().max(63).nullable(),
    senza_tipo: z.object({ cosa: z.string().max(200), perche: z.string().max(300) }).nullable(),
    nome: z.string().max(200).nullable(),
    attributi: z.array(MappingPairSchema).max(60),
  }),
  cose: z.array(z.object({ tipo: z.string().max(63), nome: z.string().max(200), attributi: z.array(MappingPairSchema).max(60), legame: z.string().max(63).nullable() })).max(20),
  verso: z.enum(['vendita', 'acquisto', 'nessuno']),
  perche: z.string().max(300).default(''),
});

/** La persona corregge nome e descrizione di un tipo (ADR 0047): la generazione non li cambia più. */
export const DataTypeEditSchema = z.object({
  etichetta: z.string().trim().min(1).max(120),
  descrizione: z.string().trim().max(1000).nullable().default(null),
});

/** Entità scritta dalla persona: nuova (rif assente) o modificata; un valore null o vuoto non cancella. */
/** Sedi e attività di un'entità dette dalla persona: per ogni sede le attività (vuoto = da attribuire); `sede` null = l'impresa. */
export const EntityLinksSchema = z
  .array(
    z.object({
      sede: z.string().uuid().nullable().describe('Id della sede; null = il rapporto è con l’impresa come soggetto'),
      attivita: z.array(z.string().uuid()).max(50).default([]).describe('Id delle attività economiche di quella sede; vuoto = da attribuire'),
    }),
  )
  .max(100);
export type EntityLinksInput = z.infer<typeof EntityLinksSchema>;

export const DataEntitySchema = z.object({
  rif: z.string().min(1).max(200).nullable().default(null),
  attributi: z.record(z.string().max(2000).nullable()),
  /** Se c'è, sono tutti i legami dell'entità secondo la persona (ADR 0057). */
  legami: EntityLinksSchema.nullable().default(null),
});

export const EntityLinksBodySchema = z.object({ legami: EntityLinksSchema });
export type DataEntityInput = z.infer<typeof DataEntitySchema>;

/** La persona dice se l'elenco di un tipo è completo (o non più). */
export const DataTypeCompleteSchema = z.object({ completo: z.boolean() });

/** Mappatura di una lettura di un programma collegato (ADR 0168): come le sue righe diventano cose dell'azienda. */
export interface ProgramMappingDto {
  /** Che cosa è ogni riga: una voce di un tipo del modello, una cosa che nessun tipo descrive, o niente. */
  riga: {
    cosa: 'voce' | 'senza_tipo' | 'niente';
    tipo: string | null;
    /** Per «senza_tipo»: il genere di cose e cosa ne dipende. */
    senza_tipo: { cosa: string; perche: string } | null;
    nome: string | null;
    attributi: Array<{ attributo: string; colonna: string }>;
  };
  /** Le altre cose dell'azienda che ogni riga nomina (la controparte, un bene, un contratto…). */
  cose: Array<{
    tipo: string;
    nome: string;
    attributi: Array<{ attributo: string; colonna: string }>;
    /** Attributo della voce della riga che punta a questa cosa (il legame), se c'è. */
    legame: string | null;
  }>;
  /** Il verso del rapporto preso dalla lettura, per i documenti di un rapporto. */
  verso: 'vendita' | 'acquisto' | 'nessuno';
  perche: string;
}

export interface ProgramMappingStateDto {
  software: string;
  lettura: string;
  etichetta: string;
  tipo_archivio: string;
  colonne: string[];
  mappatura: ProgramMappingDto | null;
  fonte: 'motore' | 'persona' | null;
  esito: { righe: number; voci: number; senza_tipo: number; errore: string | null } | null;
  applicata_il: string | null;
}

/** Pulizia dei dati di un'azienda con le regole dell'ADR 0192: profili, formati, nomi, doppioni; poi la rilettura. */
export interface DataCleanupStateDto {
  fase: 'nessuna' | 'in_corso' | 'finito' | 'errore';
  esito: {
    /** Voci con valori corretti o tolte perché il nome non è un nome. */
    valori: number;
    /** Doppioni uniti (per identità e soggetti). */
    unite: number;
    /** Tipi rimessi tra quelli da rileggere. */
    da_rileggere: number;
  } | null;
  errore: string | null;
  aggiornato_il: string | null;
}
